import { useEffect, useRef, useCallback } from 'react';

/**
 * useSmartPolling
 *
 * @param {boolean} enabled    – Start polling only when true
 * @param {() => Promise<any>} fetcher – Async function that hits your API
 * @param {() => boolean} done – Returns true when the backend says the job is finished
 * @param {number} interval    – Initial interval in ms (default 5000)
 * @param {number} backoff     – Multiply interval by this each round (default 1.5)
 * @param {number} maxWait     – Upper-bound for interval (default 60000)
 * @param {number} jitter      – 0–1 fraction of interval to randomize (default 0.25)
 * @param {number} maxRetries  – Max failed attempts before stopping (default 3)
 * @param {(error: Error) => void} onError – Callback for handling errors
 */
export function useSmartPolling({
  enabled = false,
  fetcher,
  done,
  interval = 5000,
  backoff = 1.5,
  maxWait = 60000,
  jitter = 0.25,
  maxRetries = 3,
  onError = () => {},
}) {
  const timer = useRef(null);
  const currentInterval = useRef(interval);
  const active = useRef(false);
  const retryCount = useRef(0);
  const latestFetcher = useRef(fetcher);
  const latestDone = useRef(done);

  // Update refs to ensure latest props are used
  useEffect(() => {
    latestFetcher.current = fetcher;
    latestDone.current = done;
  }, [fetcher, done]);

  const clear = useCallback(() => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
  }, []);

  const schedule = useCallback(async () => {
    if (!active.current || latestDone.current()) return;

    const jitterMs = currentInterval.current * jitter * Math.random();
    timer.current = setTimeout(async () => {
      try {
        await latestFetcher.current();
        retryCount.current = 0; // Reset retries on success
      } catch (error) {
        retryCount.current += 1;
        onError(error);

        if (retryCount.current >= maxRetries) {
          clear();
          onError(new Error(`Stopped polling after ${maxRetries} failed attempts`));
          return;
        }
      } finally {
        if (!active.current || latestDone.current()) return;

        currentInterval.current = Math.min(
          currentInterval.current * backoff,
          maxWait
        );
        schedule();
      }
    }, currentInterval.current + jitterMs);
  }, [backoff, maxWait, jitter, maxRetries, onError]);

  useEffect(() => {
    if (!enabled || done()) return; // Skip if not enabled or already done

    active.current = true;
    currentInterval.current = interval;
    retryCount.current = 0;

    const onVisibility = () => {
      if (document.hidden) {
        clear(); // Pause while hidden
      } else if (active.current && !latestDone.current()) {
        currentInterval.current = interval; // Reset interval on focus
        schedule();
      }
    };

    document.addEventListener('visibilitychange', onVisibility);
    schedule();

    return () => {
      active.current = false;
      clear();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [enabled, interval, schedule, done]);

  // Reset interval and retry count when interval prop changes
  useEffect(() => {
    currentInterval.current = interval;
    retryCount.current = 0;
  }, [interval]);
}